import React, { useState } from 'react';
import { signUp } from 'aws-amplify/auth';
import * as formik from 'formik';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import ErrorMessage from '../../ErrorMessage';
import { EyeIcon, EyeOffIcon } from '../../../assets/icons';
import { useRegisterContext } from '../RegisterContext';

function getPasswordStrength(password) {
  if (!password) {
    return {
      level: 0,
      label: '',
      colorClass: 'bg-[#E2EAF0]',
      textClass: 'text-[#7A9AB0]',
    };
  }

  const hasMixedCase = /[a-z]/.test(password) && /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);
  const varietyScore = [hasMixedCase, hasNumber, hasSymbol].filter(Boolean).length;
  const { length } = password;

  if (length < 8) {
    return {
      level: 1,
      label: 'Weak',
      colorClass: 'bg-[#C0392B]',
      textClass: 'text-[#C0392B]',
    };
  }

  if (length >= 20 && varietyScore >= 1) {
    return {
      level: 4,
      label: 'Strong',
      colorClass: 'bg-[#1A7A4A]',
      textClass: 'text-[#1A7A4A]',
    };
  }

  if (length >= 12 && varietyScore === 3) {
    return {
      level: 4,
      label: 'Strong',
      colorClass: 'bg-[#1A7A4A]',
      textClass: 'text-[#1A7A4A]',
    };
  }

  if (length >= 12 && varietyScore >= 1) {
    return {
      level: 3,
      label: 'Good',
      colorClass: 'bg-[#2980B9]',
      textClass: 'text-[#2E6BAD]',
    };
  }

  if (length >= 16) {
    return {
      level: 3,
      label: 'Good',
      colorClass: 'bg-[#2980B9]',
      textClass: 'text-[#2E6BAD]',
    };
  }

  return {
    level: 2,
    label: 'Almost there',
    colorClass: 'bg-[#E67E22]',
    textClass: 'text-[#B8761A]',
  };
}

function Step1({ nextStep }) {
  const { Formik } = formik;
  const { setSignUpUsername } = useRegisterContext();
  const [error, setError] = useState();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const onSubmit = async (values) => {
    try {
      const email = values.email.trim().toLowerCase();
      await signUp({
        username: email,
        password: values.password,
        options: {
          userAttributes: {
            given_name: values.firstName,
            family_name: values.lastName,
            email,
          },
        },
      });
      setSignUpUsername(email);
      nextStep();
    } catch (err) {
      const name = err?.name || '';
      const msg = err?.message || '';
      if (name === 'UsernameExistsException' || /already exists|user already/i.test(msg)) {
        setError({
          name: 'UsernameExistsException',
          message: 'An account with this email already exists.',
        });
        return;
      }
      setError(msg || name || 'Sign up failed');
    }
  };

  const schema = yup.object().shape({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    email: yup.string().required('Email is required').email('Not a valid email'),
    password: yup.string().required('Password is required'),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password'), null], 'Passwords do not match')
      .required('Password confirmation is required'),
    terms: yup.bool().required('Agreement is required').oneOf([true], 'Agreement is required'),
  });

  return (
    <div>
      <Formik
        validationSchema={schema}
        initialValues={{
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          confirmPassword: '',
          terms: false,
        }}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={onSubmit}
      >
        {({ handleChange, handleSubmit, values, errors }) => {
          const inputClass = (fieldError, withIcon = false) =>
            `flex h-12 w-full rounded-[10px] border-[1.5px] ${
              fieldError ? 'border-[#C0392B]' : 'border-[#DDE6EF]'
            } bg-white px-3.5 py-3 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10 ${
              withIcon ? 'pr-11' : ''
            }`;
          const labelClass = 'block text-[11.5px] font-semibold text-[#7A9AB0]';
          const errorClass = 'mt-1 text-xs text-[#C0392B]';
          const passwordStrength = getPasswordStrength(values.password);

          return (
            <div>
              <div className='px-6 pb-1 pt-8 sm:px-12 sm:pt-11'>
                <h3 className='font-serif text-2xl font-normal leading-tight text-[#0B1929]'>Open your HansePay account.</h3>
                <p className='mt-2 text-sm leading-6 text-[#7A9AB0]'>Takes about 10 minutes.</p>
              </div>
              <form className='space-y-4 px-6 pb-8 pt-5 sm:px-12 sm:pb-11' onSubmit={handleSubmit}>
                <div className='grid gap-3 sm:grid-cols-2'>
                  <div className='space-y-1.5'>
                    <label htmlFor='formFirstName' className={labelClass}>
                      First name
                    </label>
                    <input
                      id='formFirstName'
                      type='text'
                      name='firstName'
                      value={values.firstName}
                      onChange={handleChange}
                      autoComplete='given-name'
                      className={inputClass(errors.firstName)}
                    />
                    {errors.firstName && <p className={errorClass}>{errors.firstName}</p>}
                  </div>
                  <div className='space-y-1.5'>
                    <label htmlFor='formLastName' className={labelClass}>
                      Last name
                    </label>
                    <input
                      id='formLastName'
                      type='text'
                      name='lastName'
                      value={values.lastName}
                      onChange={handleChange}
                      autoComplete='family-name'
                      className={inputClass(errors.lastName)}
                    />
                    {errors.lastName && <p className={errorClass}>{errors.lastName}</p>}
                  </div>
                </div>
                <div className='space-y-1.5'>
                  <label htmlFor='formEmail' className={labelClass}>
                    Email address
                  </label>
                  <input
                    id='formEmail'
                    type='email'
                    name='email'
                    value={values.email}
                    onChange={handleChange}
                    autoComplete='email'
                    placeholder='you@example.com'
                    className={inputClass(errors.email)}
                  />
                  {errors.email && <p className={errorClass}>{errors.email}</p>}
                </div>
                <div className='space-y-1.5'>
                  <label htmlFor='formPassword' className={labelClass}>
                    Password
                  </label>
                  <div className='relative'>
                    <input
                      id='formPassword'
                      type={showPassword ? 'text' : 'password'}
                      name='password'
                      value={values.password}
                      onChange={handleChange}
                      autoComplete='new-password'
                      placeholder='Min. 8 characters'
                      className={inputClass(errors.password, true)}
                    />
                    <button
                      type='button'
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-[#7A9AB0] transition hover:text-[#2E6BAD] focus:outline-none'
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOffIcon className='w-4 h-4' /> : <EyeIcon className='w-4 h-4' />}
                    </button>
                  </div>
                  <div className='flex gap-1.5 pt-1'>
                    {[0, 1, 2, 3].map((segment) => (
                      <span
                        key={segment}
                        className={`h-[3px] flex-1 rounded-full ${passwordStrength.level > segment ? passwordStrength.colorClass : 'bg-[#E2EAF0]'}`}
                      />
                    ))}
                  </div>
                  <div className={`h-4 text-[11.5px] ${passwordStrength.textClass}`}>{passwordStrength.label}</div>
                  {errors.password && <p className={errorClass}>{errors.password}</p>}
                </div>
                <div className='space-y-1.5'>
                  <label htmlFor='formConfirmPassword' className={labelClass}>
                    Confirm password
                  </label>
                  <div className='relative'>
                    <input
                      id='formConfirmPassword'
                      type={showConfirmPassword ? 'text' : 'password'}
                      name='confirmPassword'
                      value={values.confirmPassword}
                      onChange={handleChange}
                      autoComplete='new-password'
                      placeholder='Repeat your password'
                      className={inputClass(errors.confirmPassword, true)}
                    />
                    <button
                      type='button'
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-[#7A9AB0] transition hover:text-[#2E6BAD] focus:outline-none'
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOffIcon className='w-4 h-4' /> : <EyeIcon className='w-4 h-4' />}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className={errorClass}>{errors.confirmPassword}</p>}
                </div>
                <div className='flex items-start gap-2.5 pt-1'>
                  <input
                    type='checkbox'
                    id='terms'
                    name='terms'
                    checked={values.terms}
                    onChange={handleChange}
                    className='mt-0.5 h-4 w-4 shrink-0 rounded border-[#DDE6EF] accent-[#1E4E80]'
                  />
                  <label htmlFor='terms' className='text-[13px] leading-5 text-[#3D5A73]'>
                    I agree to the{' '}
                    <a
                      href='https://www.hansepay.de/legal#terms'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-[#2E6BAD] hover:underline'
                    >
                      Terms of service
                    </a>{' '}
                    and{' '}
                    <a
                      href='https://www.hansepay.de/legal#privacy'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-[#2E6BAD] hover:underline'
                    >
                      Privacy Policy
                    </a>{' '}
                    including{' '}
                    <a
                      href='https://www.hansepay.de/legal#cookies'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-[#2E6BAD] hover:underline'
                    >
                      Cookies
                    </a>
                    .
                  </label>
                </div>
                {errors.terms && <p className={errorClass}>{errors.terms}</p>}
                <button
                  className='inline-flex h-12 w-full items-center justify-center rounded-full bg-[#0B1929] px-7 text-sm font-semibold text-white transition hover:bg-[#0F2540] disabled:cursor-not-allowed disabled:bg-[#B8C9D9]'
                  type='submit'
                  disabled={!values.firstName || !values.lastName || !values.email || !values.password || !values.confirmPassword || !values.terms}
                >
                  Create account
                </button>
                {error?.name === 'UsernameExistsException' ? (
                  <ErrorMessage
                    className='form-error-display'
                    title='Account already exists'
                    error={error}
                    description={
                      <>
                        An account with this email already exists. <a href='/login'>Log in instead</a>, or use a different email address.
                      </>
                    }
                  />
                ) : (
                  error && <ErrorMessage error={error} className='form-error-display' />
                )}
                <div className='flex items-center justify-center gap-2 pt-1'>
                  <p className='text-sm text-[#7A9AB0]'>Already have an account?</p>
                  <a href='/login' className='text-sm font-medium text-[#2E6BAD] hover:underline'>
                    Sign in
                  </a>
                </div>
              </form>
            </div>
          );
        }}
      </Formik>
    </div>
  );
}

Step1.propTypes = {
  nextStep: PropTypes.func,
};

export default Step1;
